import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import { OpenInNew as OpenInNewIcon, Refresh as RefreshIcon } from '@mui/icons-material';
import { useAuthUser } from '../../contexts/AuthUserContext';
import olxService, {
  OLX_CATEGORY_MOTORCYCLE,
  type OlxForm,
  type OlxListing,
  type OlxListingStatus,
  type OlxOption,
  type OlxVehicleInfo,
} from '../../services/olxService';

const EMPTY_FORM: OlxForm = {
  subject: '',
  body: '',
  price: '',
  vehicle_brand: '',
  vehicle_model: '',
  vehicle_version: '',
  gearbox: '',
  fuel: '',
  cubiccms: '',
  car_steering: '',
  motorpower: '',
  carcolor: '',
  doors: '',
};

const STATUS: Record<OlxListingStatus, { label: string; color: 'default' | 'success' | 'warning' | 'error' }> = {
  pending: { label: 'Em análise', color: 'warning' },
  queued: { label: 'Será ativado em breve', color: 'warning' },
  accepted: { label: 'Publicado', color: 'success' },
  refused: { label: 'Recusado', color: 'error' },
  error: { label: 'Com erro', color: 'error' },
  removing: { label: 'Removendo', color: 'warning' },
  removed: { label: 'Removido', color: 'default' },
};

type Choice = [string, string];

const CAR_FUEL: Choice[] = [['1', 'Gasolina'], ['2', 'Álcool'], ['3', 'Flex'], ['5', 'Diesel'], ['6', 'Híbrido'], ['7', 'Elétrico']];
const MOTO_FUEL: Choice[] = [['1', 'Gasolina'], ['2', 'Álcool'], ['3', 'Flex'], ['4', 'Diesel'], ['5', 'Híbrido'], ['6', 'Elétrico']];
const CAR_GEARBOX: Choice[] = [['1', 'Manual'], ['2', 'Automático'], ['3', 'Semi-automático'], ['4', 'Automatizado']];
const MOTO_GEARBOX: Choice[] = CAR_GEARBOX.slice(0, 3);
const STEERING: Choice[] = [['1', 'Hidráulica'], ['2', 'Elétrica'], ['3', 'Mecânica'], ['5', 'Eletro-hidráulica']];
const MOTOR: Choice[] = [
  ['1', '1.0'], ['2', '1.2'], ['3', '1.3'], ['4', '1.4'], ['5', '1.5'], ['6', '1.6'],
  ['7', '1.7'], ['8', '1.8'], ['9', '1.9'], ['10', '2.0 a 2.9'], ['11', '3.0 a 3.9'], ['12', '4.0 ou mais'],
];
const COLOR: Choice[] = [
  ['1', 'Preto'], ['2', 'Branco'], ['3', 'Prata'], ['4', 'Vermelho'], ['5', 'Cinza'],
  ['6', 'Azul'], ['7', 'Amarelo'], ['8', 'Verde'], ['9', 'Laranja'], ['10', 'Outra'],
];
const DOORS: Choice[] = [['1', '2 portas'], ['3', '3 portas'], ['2', '4 portas']];

const errorMessage = (e: any, fallback: string) => {
  const errors = e?.response?.data?.errors;
  const first = errors ? (Object.values(errors)[0] as string[] | undefined)?.[0] : undefined;
  return first || e?.response?.data?.message || fallback;
};

interface Props {
  open: boolean;
  vehicleId: number | string;
  onClose: () => void;
}

const OlxPublishDialog: React.FC<Props> = ({ open, vehicleId, onClose }) => {
  const { isAdmin } = useAuthUser();
  const [info, setInfo] = useState<OlxVehicleInfo | null>(null);
  const [listing, setListing] = useState<OlxListing | null>(null);
  const [form, setForm] = useState<OlxForm>(EMPTY_FORM);
  const [brands, setBrands] = useState<OlxOption[]>([]);
  const [models, setModels] = useState<OlxOption[]>([]);
  const [versions, setVersions] = useState<OlxOption[]>([]);
  const [cubicCms, setCubicCms] = useState<OlxOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isMoto = info?.category === OLX_CATEGORY_MOTORCYCLE;
  const catalogType = isMoto ? 'moto' : 'car';
  const ready = !!info?.connection.connected && !!info.connection.zipcode && !!info.connection.phone;
  const canPublish = ready && !!info?.supported && info.missing.length === 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await olxService.getVehicleInfo(vehicleId);
      setInfo(data);
      setListing(data.listing);
      // Opcionais não informados voltam como null da última publicação.
      const saved = Object.fromEntries(Object.entries(data.defaults).filter(([, value]) => value != null));
      setForm({ ...EMPTY_FORM, ...saved });
    } catch (e: any) {
      setError(errorMessage(e, 'Não foi possível carregar os dados da OLX.'));
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => {
    if (open) void load();
  }, [open, load]);

  const catalogError = (e: any) => setError(errorMessage(e, 'Não foi possível carregar o catálogo da OLX.'));

  useEffect(() => {
    if (!open || !canPublish) return;
    olxService.getCatalog(catalogType).then(setBrands).catch(catalogError);
    if (isMoto) olxService.getCubicCms().then(setCubicCms).catch(catalogError);
  }, [open, canPublish, catalogType, isMoto]);

  useEffect(() => {
    setModels([]);
    if (!canPublish || !form.vehicle_brand) return;
    olxService.getCatalog(catalogType, form.vehicle_brand).then(setModels).catch(catalogError);
  }, [canPublish, catalogType, form.vehicle_brand]);

  useEffect(() => {
    setVersions([]);
    if (!canPublish || !form.vehicle_brand || !form.vehicle_model) return;
    olxService.getCatalog(catalogType, form.vehicle_brand, form.vehicle_model).then(setVersions).catch(catalogError);
  }, [canPublish, catalogType, form.vehicle_brand, form.vehicle_model]);

  const set = (patch: Partial<OlxForm>) => setForm((prev) => ({ ...prev, ...patch }));

  const run = async (action: () => Promise<OlxListing>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      setListing(await action());
    } catch (e: any) {
      if (e?.response?.data?.listing) setListing(e.response.data.listing);
      setError(errorMessage(e, fallback));
    } finally {
      setBusy(false);
    }
  };

  const catalogField = (
    label: string,
    options: OlxOption[],
    value: string,
    onChange: (id: string) => void,
    disabled = false
  ) => (
    <Autocomplete
      options={options}
      disabled={disabled}
      value={options.find((o) => o.id === value) ?? null}
      getOptionLabel={(o) => o.name}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      onChange={(_, option) => onChange(option?.id ?? '')}
      noOptionsText="Nada encontrado"
      renderInput={(params) => <TextField {...params} required label={label} size="small" />}
    />
  );

  const selectField = (label: string, field: keyof OlxForm, choices: Choice[], required = false) => (
    <TextField
      select
      fullWidth
      size="small"
      required={required}
      label={label}
      value={form[field]}
      onChange={(e) => set({ [field]: e.target.value })}
    >
      {!required && <MenuItem value="">Não informar</MenuItem>}
      {choices.map(([id, name]) => (
        <MenuItem key={id} value={id}>
          {name}
        </MenuItem>
      ))}
    </TextField>
  );

  const isPublished = !!listing && !['removed', 'error'].includes(listing.status);
  const formValid =
    !!form.subject.trim() &&
    !!form.body.trim() &&
    Number(form.price) > 0 &&
    !!form.vehicle_brand &&
    !!form.vehicle_model &&
    !!form.vehicle_version &&
    (!isMoto || (!!form.fuel && !!form.cubiccms));

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Publicar na OLX</DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            {listing && (
              <Alert
                severity={STATUS[listing.status].color === 'error' ? 'error' : 'info'}
                icon={false}
                sx={{ mb: 2 }}
                action={
                  <Box sx={{ display: 'flex', gap: 1 }}>
                    {listing.url && (
                      <Button size="small" color="inherit" endIcon={<OpenInNewIcon />} href={listing.url} target="_blank" rel="noopener noreferrer">
                        Ver anúncio
                      </Button>
                    )}
                    <Button
                      size="small"
                      color="inherit"
                      startIcon={<RefreshIcon />}
                      disabled={busy}
                      onClick={() => void run(() => olxService.refresh(vehicleId), 'Não foi possível consultar a OLX.')}
                    >
                      Atualizar
                    </Button>
                  </Box>
                }
              >
                <Chip size="small" color={STATUS[listing.status].color} label={STATUS[listing.status].label} />
                {listing.message && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {listing.message}
                  </Typography>
                )}
              </Alert>
            )}

            {info && !info.supported && <Alert severity="warning">A OLX só recebe carros e motos por esta integração.</Alert>}

            {info && info.supported && !ready && (
              <Alert
                severity="warning"
                action={
                  isAdmin ? (
                    <Button color="inherit" size="small" component={Link} to="/integracoes/olx">
                      Configurar
                    </Button>
                  ) : undefined
                }
              >
                {isAdmin
                  ? 'Conecte a conta OLX e informe o CEP e o telefone dos anúncios.'
                  : 'Peça a um administrador para configurar a integração com a OLX.'}
              </Alert>
            )}

            {info && info.supported && info.missing.length > 0 && (
              <Alert severity="warning" sx={{ mt: ready ? 0 : 2 }}>
                Antes de publicar, complete o cadastro do veículo:
                <ul style={{ margin: '4px 0 0', paddingLeft: 18 }}>
                  {info.missing.map((m) => (
                    <li key={m}>{m}</li>
                  ))}
                </ul>
              </Alert>
            )}

            {canPublish && (
              <Grid container spacing={2}>
                <Grid item xs={12} sm={8}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    label="Título"
                    value={form.subject}
                    inputProps={{ maxLength: 90 }}
                    helperText={isMoto ? undefined : 'Para carros, a OLX monta o título automaticamente.'}
                    onChange={(e) => set({ subject: e.target.value })}
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    required
                    size="small"
                    type="number"
                    label="Preço (R$)"
                    value={form.price}
                    onChange={(e) => set({ price: e.target.value === '' ? '' : Math.floor(Number(e.target.value)) })}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    required
                    multiline
                    minRows={4}
                    size="small"
                    label="Descrição"
                    value={form.body}
                    inputProps={{ maxLength: 6000 }}
                    onChange={(e) => set({ body: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} sm={4}>
                  {catalogField('Marca (OLX)', brands, form.vehicle_brand, (id) =>
                    set({ vehicle_brand: id, vehicle_model: '', vehicle_version: '' })
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {catalogField(
                    'Modelo (OLX)',
                    models,
                    form.vehicle_model,
                    (id) => set({ vehicle_model: id, vehicle_version: '' }),
                    !form.vehicle_brand
                  )}
                </Grid>
                <Grid item xs={12} sm={4}>
                  {catalogField(
                    'Versão (OLX)',
                    versions,
                    form.vehicle_version,
                    (id) => set({ vehicle_version: id }),
                    !form.vehicle_model
                  )}
                </Grid>

                <Grid item xs={6} sm={4}>
                  {selectField('Câmbio', 'gearbox', isMoto ? MOTO_GEARBOX : CAR_GEARBOX)}
                </Grid>
                <Grid item xs={6} sm={4}>
                  {selectField('Combustível', 'fuel', isMoto ? MOTO_FUEL : CAR_FUEL, isMoto)}
                </Grid>
                <Grid item xs={6} sm={4}>
                  {selectField('Cor', 'carcolor', COLOR)}
                </Grid>
                {isMoto ? (
                  <Grid item xs={6} sm={4}>
                    {selectField('Cilindrada', 'cubiccms', cubicCms.map((c) => [c.id, c.name] as Choice), true)}
                  </Grid>
                ) : (
                  <>
                    <Grid item xs={6} sm={4}>
                      {selectField('Direção', 'car_steering', STEERING)}
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      {selectField('Motor', 'motorpower', MOTOR)}
                    </Grid>
                    <Grid item xs={6} sm={4}>
                      {selectField('Portas', 'doors', DOORS)}
                    </Grid>
                  </>
                )}

                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    {info?.image_count} foto(s) serão enviadas. Ano, quilometragem{isMoto ? '' : ' e placa'} vêm do cadastro do
                    veículo. {!isMoto && 'A OLX não mostra a placa no anúncio.'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        {isPublished && listing?.status !== 'removing' && (
          <Button
            color="error"
            disabled={busy}
            sx={{ mr: 'auto' }}
            onClick={() => {
              if (window.confirm('Remover este anúncio da OLX?')) {
                void run(() => olxService.remove(vehicleId), 'Não foi possível remover o anúncio.');
              }
            }}
          >
            Remover da OLX
          </Button>
        )}
        <Button onClick={onClose} disabled={busy}>
          Fechar
        </Button>
        {canPublish && (
          <Button
            variant="contained"
            disabled={busy || !formValid}
            onClick={() => void run(() => olxService.publish(vehicleId, form), 'Não foi possível enviar para a OLX.')}
          >
            {isPublished ? 'Atualizar anúncio' : 'Publicar'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default OlxPublishDialog;
